import React, { useEffect, useMemo, useState } from "react";
import "./RateCalculation.scss";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { Spinner } from "spot-component-library/lib/components/basic/spinner/Spinner";
import { DateField } from "spot-component-library/lib/components/form/dateField/DateField";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import FormatNumber, { formattedNumber } from "../../components/FormatNumber";
import ToolTip from "../../components/ToolTip/ToolTip";
import { useSettlementFee } from "../../contexts/SettlementFeeContext";
import { useCurrency, useRateAgreementTenantConfiguration } from "../../contexts/TenantContext";
import { Balance } from "../../types/Balance";
import { RateAgreementOptions } from "../../types/RateAgreementOptions";
import { RateData } from "../../types/RateData";
import { calculateCompleteBalance, calculateRemainingBalance } from "../../utils/helper";
import { RateCalculationRenderer } from "./RateCalculationRenderer/RateCalculationRenderer";
import { getStrategy } from "./RateCalculationStrategy";

type RateCalculationProps = {
    onNext: (rateData: RateData) => void;
    rateData: RateData;
    debtBalance: Balance;
    rateAgreementOptions: RateAgreementOptions;
    stateChanger?: any;
};

export const RateCalculation = ({ onNext, rateData: initialRateData, debtBalance, rateAgreementOptions, stateChanger }: RateCalculationProps) => {
    const { t } = useTranslation();
    const {
        register,
        setValue,
        formState: { errors, isValid },
        trigger,
        control,
    } = useForm({ mode: "onBlur" });

    const currency = useCurrency();
    const configuration = useRateAgreementTenantConfiguration();
    const settlementFee = useSettlementFee();

    const [rateData, setRateData] = useState<RateData>(initialRateData);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [isMaxRateAlerting, setIsMaxRateAlerting] = useState<boolean>(false);

    const rateCalculationStrategy = useMemo(() => getStrategy(configuration?.rateCalculationType), [configuration?.rateCalculationType]);
    const completeBalance = useMemo(() => calculateCompleteBalance(debtBalance, settlementFee.value), [debtBalance, settlementFee.value]);
    const remainingBalance = useMemo(() => calculateRemainingBalance(rateData.rateAmount, rateData.rateNumber, completeBalance), [rateData, completeBalance]);

    // apply initial rate amount when settlement fee was initialized
    useEffect(() => {
        const shouldApplyInitialRateAmount = !isInitialized && settlementFee.isInitialized;
        if (shouldApplyInitialRateAmount) {
            const calculatedRate = calculateRate({ rateAmount: initialRateData.rateAmount });
            refreshRateDataValues(calculatedRate);
            setIsInitialized(true);
        }
    }, [initialRateData, settlementFee.isInitialized, isInitialized]);

    // update rate amount when settlement fee has changed
    useEffect(() => {
        if (isInitialized) {
            const calculatedRate = calculateRate({ numberOfRates: rateData.rateNumber });
            refreshRateDataValues(calculatedRate);
        }
    }, [settlementFee.value]);

    const calculateRate = ({ numberOfRates, rateAmount }: { numberOfRates?: number; rateAmount?: number }): RateData => {
        let numberOfRatesCalc = numberOfRates;
        let rateAmountCalc = rateAmount;

        if (numberOfRatesCalc) {
            // number of rates was updated, calculating rate amount
            rateAmountCalc = isNaN(numberOfRatesCalc) ? rateData.rateAmount : rateCalculationStrategy.calculateRateAmount(completeBalance, numberOfRatesCalc);
        } else if (rateAmountCalc) {
            // rate amount was updated, calculating number of rates
            numberOfRatesCalc = isNaN(rateAmountCalc) ? rateData.rateNumber : rateCalculationStrategy.calculateNumberOfRates(completeBalance, rateAmountCalc);
        }

        // limit to max number of rates
        if (numberOfRatesCalc && numberOfRatesCalc > rateAgreementOptions.maxNumberOfRates) {
            numberOfRatesCalc = rateAgreementOptions.maxNumberOfRates;
        }

        // limit to max rate amount
        if (rateAmountCalc && rateAmountCalc > rateAgreementOptions.maxRate) {
            rateAmountCalc = rateAgreementOptions.maxRate;
        }

        return { ...rateData, rateAmount: rateAmountCalc, rateNumber: numberOfRatesCalc };
    };

    const refreshRateDataValues = (updatedRateData: RateData) => {
        setRateData(updatedRateData);
        setValue("rateAmount", updatedRateData.rateAmount, { shouldValidate: true });
        setValue("rateNumber", updatedRateData.rateNumber, { shouldValidate: true });
        if (updatedRateData.dateOfFirstPay) {
            setValue("paymentDate", updatedRateData.dateOfFirstPay, { shouldValidate: true });
        }

        const remainingAmount = calculateRemainingBalance(updatedRateData.rateAmount, updatedRateData.rateNumber, completeBalance);
        stateChanger(remainingAmount);
        setIsMaxRateAlerting(updatedRateData.rateAmount !== undefined && remainingAmount !== undefined && remainingAmount > updatedRateData.rateAmount);
    };

    const handleFormInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = target;

        if (!value) {
            console.warn(`Empty ${name} input: ${value}`);
            return;
        }

        if (name === "rateNumber") {
            handleRateNumberChange(rateAgreementOptions.maxNumberOfRates < Number(value) ? rateAgreementOptions.maxNumberOfRates : Number(value));
        } else if (name === "rateAmount") {
            handleRateAmountChange(Number(value));
        } else {
            throw new Error("handleFormInputChange expects input name rateNumber or rateAmount");
        }
    };

    const handleRateNumberChange = (numberOfRates: number) => {
        const updatedRateData = calculateRate({ numberOfRates });

        if (updatedRateData) {
            refreshRateDataValues(updatedRateData);
        }

        settlementFee.update(updatedRateData);
    };

    const handleRateAmountChange = (rateAmount: number) => {
        const updatedRateData = calculateRate({ rateAmount });

        if (updatedRateData) {
            refreshRateDataValues(updatedRateData);
        }

        settlementFee.update(updatedRateData);
    };

    const handleDateChange = async (newDate: Date) => {
        setRateData({ ...rateData, dateOfFirstPay: newDate });
        await trigger("paymentDate");
    };

    return (
        <div data-test="rateCalculation" id="rateCalculation">
            <Panel>
                <RateCalculationRenderer
                    errors={errors}
                    isMaxRateAlerting={isMaxRateAlerting}
                    rateAgreementOptions={rateAgreementOptions}
                    rateData={rateData}
                    register={register}
                    type={configuration?.rateCalculationType}
                    onInputChange={handleFormInputChange}
                    onChangeRateAmount={handleRateAmountChange}
                    onChangeRateNumber={handleRateNumberChange}>
                    <div className="whiteBlock">
                        <div className="row-wrapper">
                            <div>
                                <span className="text-middle">{t("CONFIGURATION_PAGE.RemainingBalance")}</span>
                                <ToolTip
                                    text={t("CONFIGURATION_PAGE.RATE_OF_INTEREST_TOOLTIP", { rateOfInterest: formattedNumber(rateAgreementOptions.rateOfInterest) })}
                                    dataTestIdTrigger="rateOfInterestTooltip-trigger"
                                    dataTestIdContent="rateOfInterestTooltip-content"
                                />
                            </div>
                            <div data-test="remainingBalance">
                                {settlementFee.requestIsPending ? <Spinner isLoading size={20} /> : <FormatNumber number={remainingBalance} currency={currency} />}
                            </div>
                        </div>
                    </div>
                    <div className="payment-date-row">
                        <div>
                            <p>
                                <b>{t("CONFIGURATION_PAGE.HeadlineZahlungstermin")}</b>
                            </p>
                        </div>
                        <div>
                            <DateField
                                id="paymentDate"
                                name="paymentDate"
                                value={rateData.dateOfFirstPay}
                                control={control}
                                errors={errors}
                                required
                                onChange={handleDateChange}
                                minDate={rateAgreementOptions.earliestPaymentDay}
                                maxDate={rateAgreementOptions.latestPaymentDay}
                                weekendsDisabled
                            />
                        </div>
                    </div>
                </RateCalculationRenderer>
            </Panel>
            <div className="buttonBlock">
                <SPOTButton id="buttonBack" type="secondary" dataTest="buttonBack" label={t("CONFIGURATION_PAGE.Zurueck")} onClick={() => window.history.back()} />
                <SPOTButton
                    id="continueToPaymentForm"
                    type="primary"
                    dataTest="continueToPaymentForm"
                    label={t("CONFIGURATION_PAGE.Zahlart")}
                    onClick={() => onNext(rateData)}
                    disabled={!isValid}
                />
            </div>
        </div>
    );
};
